/**
 * Help Text Renderer Utility
 *
 * Provides functionality to render help text with special styling for
 * statistical information (text in parentheses starting with "Age average:").
 */

import React from "react";

/**
 * Renders help text with special styling for statistical information
 *
 * Statistical text (enclosed in parentheses starting with "Age average:")
 * is rendered in italic-bold font that is 20% smaller than the base text.
 *
 * @param helpText - The help text string to render
 * @returns React element with styled help text
 *
 * @example
 * renderHelpText("This is help. (Age average: $50,000)")
 * // Renders: "This is help. " with "(Age average: $50,000)" in italic-bold, smaller font
 */
export function renderHelpText(helpText: string): React.ReactNode {
  // Pattern to match statistical text: "(Age average: ...)"
  const statisticPattern = /(\(Age average:[^)]+\))/;
  const match = helpText.match(statisticPattern);

  if (!match) {
    // No statistical text found, return as-is
    return helpText;
  }

  const statisticText = match[1];
  const parts = helpText.split(statisticPattern);

  return (
    <>
      {parts.map((part, index) => {
        if (part === statisticText) {
          // Apply special styling to statistical text
          return (
            <span
              key={index}
              className="help-text-bold-italic"
              style={{ fontSize: "0.8em" }}
            >
              {part}
            </span>
          );
        }
        return part;
      })}
    </>
  );
}
